import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

const FAQ_DATA = [
  {
    category: "About The Signal",
    questions: [
      {
        q: "What is The Signal?",
        a: "The Signal is a step-by-step glow-up system for men. It covers everything from skin and hair improvement to confidence and body language. No fluff, just what actually works."
      },
      {
        q: "Who is this for?",
        a: "Men who want to improve how they look and present themselves. Whether you're starting from scratch or already working on yourself—if you want a clear system to follow, this is for you."
      },
      {
        q: "Who is this NOT for?",
        a: "Anyone looking for overnight results or magic pills. Real improvement takes consistent effort. If you're not willing to put in the work, save your money."
      },
      {
        q: "Why does appearance matter?",
        a: "First impressions are made in seconds. How you look affects career opportunities, relationships, and how people treat you. It's not vanity—it's reality. This system helps you use that reality to your advantage."
      }
    ]
  },
  {
    category: "Products & Pricing",
    questions: [
      {
        q: "What's included in The Signal: Upgrade?",
        a: "The complete system: step-by-step glow-up guide, hair improvement techniques, skin routines, face improvement tips, body basics, confidence and body language training. You get lifetime access including all future updates."
      },
      {
        q: "What's the 1-on-1 Coaching?",
        a: "Personal accountability and guidance. Weekly check-ins on your progress, direct chat support when you need advice, customized recommendations for your situation, and access to the private community."
      },
      {
        q: "How much does it cost?",
        a: "The Signal: Upgrade is €69 (early access price — increasing soon) for lifetime access. 1-on-1 Coaching is €200/month with no commitment—cancel anytime."
      },
      {
        q: "Can I cancel the coaching subscription?",
        a: "Yes, cancel anytime. No contracts, no cancellation fees. Your access continues until the end of your billing period."
      }
    ]
  },
  {
    category: "Results & Expectations",
    questions: [
      {
        q: "What results can I expect?",
        a: "Clearer skin, better hair, sharper features, improved confidence, better body language. Results depend on where you're starting and how consistently you follow the system."
      },
      {
        q: "How long until I see changes?",
        a: "You'll start building better habits within the first week. Visible changes typically show within 4-8 weeks of consistent effort. This is a system, not a quick fix."
      },
      {
        q: "Do I need expensive products?",
        a: "No. The guide focuses on what works, not what's expensive. Many effective products are affordable. Quality technique matters more than brand names."
      }
    ]
  },
  {
    category: "Access & Support",
    questions: [
      {
        q: "How do I access the guide after purchase?",
        a: "After purchase, create an account with the same email you used at checkout. You'll have immediate access to view the guide on the website. The content is view-only to protect the material."
      },
      {
        q: "What payment methods do you accept?",
        a: "All major credit cards, debit cards, Apple Pay, Google Pay, and other payment methods through our secure checkout."
      },
      {
        q: "Is my payment secure?",
        a: "Yes. All payments are processed through Shopify's secure payment system. We never see or store your payment details."
      },
      {
        q: "How do I contact support?",
        a: "Use the contact page or DM on TikTok (@rc.vnl). Coaching subscribers get priority response through direct chat."
      }
    ]
  }
];

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function FAQ() {
  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="faq-page">
      {/* Header */}
      <section className="py-20 md:py-28 border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              Support
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Frequently Asked Questions
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-[#999999] max-w-2xl">
              Find answers to common questions about The Signal and what to expect.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {FAQ_DATA.map((category, catIdx) => (
            <motion.div 
              key={catIdx} 
              className="mb-12"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={staggerContainer}
            >
              <motion.h2 
                variants={fadeInUp}
                className="font-serif text-2xl text-white mb-6 pb-4 border-b border-[#1a1a1a]"
              >
                {category.category}
              </motion.h2>
              <Accordion type="single" collapsible className="space-y-3">
                {category.questions.map((item, qIdx) => (
                  <motion.div key={qIdx} variants={fadeInUp}>
                    <AccordionItem 
                      value={`${catIdx}-${qIdx}`}
                      className="border border-[#1a1a1a] bg-[#0f0f0f]"
                      data-testid={`faq-item-${catIdx}-${qIdx}`}
                    >
                      <AccordionTrigger className="px-6 py-4 text-left font-medium text-white hover:text-white hover:bg-[#0f0f0f] hover:no-underline transition-colors">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6 pt-0 text-[#999999] leading-relaxed">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-[#0f0f0f]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="border-l-2 border-[#D4AF37] pl-6">
            <h3 className="font-serif text-xl text-white mb-3">Disclaimer</h3>
            <p className="text-sm text-[#999999] leading-relaxed">
              The Signal provides educational content about grooming, fitness, and self-improvement. 
              Individual results vary based on effort, consistency, and individual factors. This is not medical advice. 
              Consult qualified professionals for specific health concerns.
            </p>
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 md:py-24">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-4xl mx-auto px-6 md:px-12 text-center"
        >
          <motion.h2 variants={fadeInUp} className="font-serif text-3xl text-white mb-4">
            Still have questions?
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#999999] mb-8">
            Reach out through our contact page and we'll get back to you.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/contact">
              <Button 
                className="btn-premium bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none px-8 py-4 text-sm uppercase tracking-widest"
                data-testid="contact-cta"
              >
                Contact Us
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
